import numbro from 'numbro';
import { capitalize } from 'vue';

import BudgetModes from '@/common/enums/budget-modes.enum';
import PurchaseTypesEnum from '@/common/enums/purchase-types.enum';
import { DASH, formatCurrency } from '@/globals';
import rootStore from '@/store';
import formatNumber from '@/utils/NumberFormatter';
import i18n from '@/vueI18n';

export default class CPMCalculator {
  constructor(pack, lineItem, plan) {
    this.lineItem = lineItem;
    this.package = pack;
    this.plan = plan;
    this.audienceInfo = null;

    // Only for cpm markup in plan
    this.storedEstimatedCPM = 0;
    this.storedEstimatedAverageCPM = 0;
    this.storedEstimatedAudienceCPM = 0;
    this.storedEstimatedAverageAudienceCPM = 0;
    this.eCPMWithNoMarkup = null;
    this.storedStats = null;
  }

  get entity() {
    return this.lineItem || this.package || this.plan;
  }

  get isCPMFromRateCard() {
    if (this.package != null) {
      return this.package.isCPMFromRateCard;
    }
    if (this.lineItem != null) {
      return this.lineItem.isCPMFromRateCard;
    }
    return this.plan.isCPMFromRateCard;
  }

  get cpmFixed() {
    if (this.lineItem != null) {
      //
      // CAM-2532
      //
      if (
        this.lineItem.package != null &&
        !this.lineItem.package.isCPMFromRateCard &&
        this.lineItem.CPM === this.lineItem.package.cpmFixed &&
        this.lineItem.cpmNet?.length > 0
      ) {
        // cpmNet[0].cpm includes the fees whereas the one from package do not
        return this.lineItem.cpmNet[0].cpm;
      }
      if (this.lineItem.CPM != null) {
        return this.lineItem.CPM;
      }
    }
    if (this.package != null) {
      return this.package.cpmFixed;
    }
    if (this.plan != null && this.plan.CPM != null) {
      return this.plan.CPM;
    }
    return null;
  }

  get cpmNet() {
    if (this.lineItem != null && this.lineItem.cpmNet != null && this.lineItem.cpmNet.length > 0) {
      return this.lineItem.cpmNet;
    }
    if (this.package != null && this.package.cpmNet != null && this.package.cpmNet.length > 0) {
      return this.package.cpmNet;
    }
    if (this.plan != null && this.plan.cpmNet != null && this.plan.cpmNet.length > 0) {
      return this.plan.cpmNet;
    }
    return null;
  }

  get cpmNetMin() {
    if (this.cpmNet != null) {
      return Math.min(...this.cpmNet.map((x) => (x.cpm_min == null ? x.cpm : x.cpm_min)));
    }
    return null;
  }

  get cpmNetMax() {
    if (this.cpmNet != null) {
      return Math.max(...this.cpmNet.map((x) => (x.cpm_max == null ? x.cpm : x.cpm_max)));
    }
    return null;
  }

  get cpmNetAvg() {
    if (this.cpmNet == null) {
      return null;
    }

    if (this.cpmNet.some((x) => x.cpm_avg != null)) {
      const avgCPMs = this.cpmNet.map((x) => x.cpm_avg);
      return avgCPMs.reduce((mem, x) => mem + x) / avgCPMs.length;
    }
    return null;
  }

  //
  // Used when initializing the estimated CPM.
  //
  get computedCPM() {
    if (this.cpmFixed != null && (this.package != null || this.lineItem != null || this.plan != null)) {
      return this.cpmFixed >= 0 ? [this.cpmFixed] : null;
    }
    // if (this.cpmFixed != null) {
    //   if (this.cpmFixed >= 0) {
    //     return [this.cpmFixed];
    //   }
    // }
    if (this.cpmNet != null) {
      const minCpm = this.cpmNetMin;
      const maxCpm = this.cpmNetMax;

      if (maxCpm >= 0) {
        if (minCpm !== maxCpm) {
          return [minCpm, maxCpm];
        }
        return [minCpm];
      }
      return null;
    }
    return null;
  }

  get computedAverageCPM() {
    if (this.isCPMFromRateCard) {
      return this.estimatedAverageCPM != null ? this.estimatedAverageCPM : this.cpmNetAvg;
    }
    return this.computedCPM != null
      ? this.computedCPM.reduce((cpm, x) => cpm + x) / this.computedCPM.length
      : this.estimatedAverageCPM;
  }

  get computedDiscountedAverageCPM() {
    if (this.isCPMFromRateCard) {
      return this.discountedEstimatedAverageCPM != null ? this.discountedEstimatedAverageCPM : this.cpmNetAvg;
    }

    if (this.lineItem != null && this.lineItem.totalDiscount > 0) {
      return this.computedCPM != null
        ? (100 *
            ((this.computedCPM.reduce((cpm, x) => cpm + x) / this.computedCPM.length) *
              (1 - this.lineItem.totalDiscount))) /
            100
        : this.discountedEstimatedAverageCPM;
    }

    return this.computedCPM != null
      ? (100 * (this.computedCPM.reduce((cpm, x) => cpm + x) / this.computedCPM.length)) / 100
      : this.discountedEstimatedAverageCPM;
  }

  //
  // Used in Estimated Stats when CPM is not from rate card && (no package or is supply org)
  //
  get discountedCPMFormatted() {
    if (this.computedCPM == null) {
      return DASH;
    }
    if (this.lineItem != null && this.lineItem.totalDiscount > 0) {
      return this.computedCPM.map((x) => i18n.global.n(x * (1 - this.lineItem.totalDiscount), 'currency')).join(' – ');
    }
    return this.computedCPM.map((x) => i18n.global.n(x, 'currency')).join(' – ');
  }

  //
  // Exclusively used by deals, in DealDetails and DealList page.
  //
  get cpmDescription() {
    if (this.isCPMFromRateCard) {
      return capitalize(i18n.global.t('names.perRateCard', { n: 1 }));
    }
    const currencyCode = this.package?.currencyInformation.currency;
    if (this.cpmNet != null) {
      if (this.cpmNet.length === 1) {
        return formatCurrency(this.cpmNet[0].cpm_avg || this.cpmNet[0].cpm, { currencyCode });
      }
      const cpms = this.cpmNet.map((x) => x.cpm_avg || x.cpm);
      return `${formatCurrency(Math.min(...cpms), { currencyCode })} – ${formatCurrency(Math.max(...cpms), { currencyCode })}`;
    }
    return this.computedAverageCPM != null ? formatCurrency(this.computedAverageCPM, { currencyCode }) : DASH;
  }

  get lastStoredDiscountedCPMFormatted() {
    if (this.lineItem.lastStoredDiscountedCPM == null) {
      return this.discountedEstimatedCPMFormatted;
    }
    if (this.lineItem.cpm === null || this.plan.cpm === null) {
      return capitalize(i18n.global.t('names.perRateCard', { n: 1 }));
    }
    return i18n.global.n(this.lineItem.lastStoredDiscountedCPM, 'currency');
  }

  get budgetMode() {
    if (this.lineItem != null) {
      return this.lineItem.budgetMode;
    }
    if (this.package != null) {
      return this.package.budgetMode;
    }
    return this.plan.budgetMode;
  }

  // --------------------------------------------------------------------------
  // Budget related props and methods
  //

  get budget() {
    if (this.lineItem != null) {
      return this.lineItem.budget;
    }
    if (this.plan != null) {
      return this.plan.budget;
    }
    if (this.package != null) {
      return this.package.budgetMin;
    }
    return null;
  }

  get estimatedBudget() {
    // Budget
    if (this.budgetMode === BudgetModes.BUDGET) {
      return this.budget;
    }

    // Impressions
    let cpm = this.computedDiscountedAverageCPM;
    if (this.plan?.hasMarkup && this.plan?.isOverrideMarkup) {
      cpm = this.plan.planFeeValue;
    }
    return (cpm * this.targetImpressions) / 1000;
  }

  get budgetFormatted() {
    return this.formatBudget(true);
  }

  get budgetFormattedNoCurrency() {
    return this.formatBudget(false);
  }

  get discountedBudgetFormatted() {
    return this.getFormattedDiscountedBudget();
  }

  get budgetFormattedShort() {
    switch (this.budgetMode) {
      case 'I': {
        if (this.computedAverageCPM == null || this.targetImpressions == null || this.targetImpressions === 0) {
          return DASH;
        }
        return formatNumber((this.computedAverageCPM * this.targetImpressions) / 1000, '$0.0a');
      }
      case 'H':
      case 'B': {
        return formatNumber(this.budget, '$0.0a');
      }
      default:
        return DASH;
    }
  }

  buildLineItemCPMNet(cpmInfo, entity) {
    let cpm_min = null;
    let cpm_max = null;
    let cpm_avg = null;

    const isBuyingDeals =
      entity.package ||
      entity.packageGroupID != null ||
      entity.packageGroupDealsIDs?.length > 0 ||
      entity.packageIDs?.length > 0;
    const isCpmFixedLineItem = !entity.isCPMFromRateCard && !isBuyingDeals;
    const hasDiscounts = entity.lineItemDiscounts?.length > 0;

    if (isCpmFixedLineItem || hasDiscounts) {
      return null;
    }

    if (cpmInfo.min != null) {
      cpm_min = cpmInfo.min;
      cpm_max = cpmInfo.max;
    } else if (cpmInfo.buyer_min_cpm != null) {
      cpm_min = cpmInfo.buyer_min_cpm;
      cpm_max = cpmInfo.buyer_max_cpm;
    } else if (cpmInfo.owner_min_cpm != null) {
      cpm_min = cpmInfo.owner_min_cpm;
      cpm_max = cpmInfo.owner_max_cpm;
    }

    cpm_avg = cpmInfo.average_cpm || cpmInfo.average;
    if (cpm_avg == null) {
      cpm_avg = cpmInfo.buyer_average_cpm != null ? cpmInfo.buyer_average_cpm : cpmInfo.owner_average_cpm;
    }

    return [
      {
        cpm: cpm_avg,
        cpm_min,
        cpm_max,
        cpm_avg,
      },
    ];
  }

  formatBudget(includeCurrency = true) {
    switch (this.budgetMode) {
      case 'I': {
        if (
          this.computedAverageCPM == null ||
          this.targetImpressions == null ||
          this.targetImpressions === 0 ||
          Number.isNaN(this.targetImpressions) ||
          this.computedDiscountedAverageCPM === null ||
          this.computedDiscountedAverageCPM === undefined
        ) {
          return DASH;
        }
        return formatCurrency(this.estimatedBudget, { includeSymbol: includeCurrency });
      }
      case 'H':
      case 'B': {
        return this.budget != null && !Number.isNaN(this.budget)
          ? formatCurrency(this.budget, { includeSymbol: includeCurrency })
          : DASH;
      }
      default:
        return DASH;
    }
  }

  getFormattedDiscountedBudget() {
    switch (this.budgetMode) {
      case 'I': {
        if (
          this.computedCPM == null ||
          this.computedCPM[0] == null ||
          this.targetImpressions == null ||
          this.targetImpressions === 0
        ) {
          return DASH;
        }
        return formatNumber(this.lineItem.lastStoredDiscountedCPM * (this.targetImpressions / 1000), '$0,0.00');
      }
      case 'H':
      case 'B': {
        return this.budget != null ? i18n.global.n(this.budget, 'currency') : DASH;
      }
      default:
        return DASH;
    }
  }

  getBudgetFromImpressions() {
    const formattedValue = ((this.computedAverageCPM * this.targetImpressions) / 1000).toFixed(2);
    return numbro(formattedValue).value();
  }

  estimateBudgetFromImpressions(impressions) {
    if (impressions === null || this.computedDiscountedAverageCPM == null) {
      return null;
    }
    return (this.computedDiscountedAverageCPM * impressions) / 1000;
  }

  // --------------------------------------------------------------------------
  // Impressions related props and methods
  //

  get targetImpressions() {
    if (this.lineItem != null) {
      return this.lineItem.targetImpressions;
    }
    if (this.package != null) {
      return this.package.marketTargetedImpressionsMin;
    }
    return this.plan.targetImpressions;
  }

  get estimatedTargetImpressionsFormatted() {
    return this.getFormattedEstimatedTargetImpressions('0,0');
  }

  get estimatedTargetImpressions() {
    let impressions = null;
    switch (this.budgetMode) {
      case 'B': {
        if (
          this.computedAverageCPM == null ||
          this.computedAverageCPM === 0 ||
          this.budget == null ||
          this.budget === 0
        ) {
          break;
        }
        let cpm = this.computedDiscountedAverageCPM;
        if (this.plan?.hasMarkup && this.plan?.isOverrideMarkup) {
          cpm = this.plan.planFeeValue;
        }
        impressions = Math.round((this.budget / cpm) * 1000);
        if (
          (this.lineItem != null || this.plan != null) &&
          impressions !== this.targetImpressions &&
          this.entity.purchaseType !== PurchaseTypesEnum.CADENCE // TODO - Double check this
        ) {
          this.updateTargetImpressions(impressions);
        }
        break;
      }
      case 'H':
      case 'I': {
        impressions = this.targetImpressions;
        break;
      }
      default:
        break;
    }
    return impressions;
  }

  estimateImpressionsFromBudget(budget) {
    if (budget === null || this.computedDiscountedAverageCPM == null || this.computedDiscountedAverageCPM === 0) {
      return null;
    }

    return (budget / this.computedDiscountedAverageCPM) * 1000;
  }

  getFormattedEstimatedTargetImpressions(fmt) {
    return this.estimatedTargetImpressions != null ? formatNumber(this.estimatedTargetImpressions, fmt) : DASH;
  }

  calculateCPMFromRateCard(cpmInfo = null, audienceInfo = null) {
    // BE removed cpmNet from /GET lineitems
    // To not break the code, we built it on our own using /calculatecpm response
    if (this.lineItem != null && cpmInfo != null) {
      this.lineItem.cpmNet = this.buildLineItemCPMNet(cpmInfo, this.lineItem);
    }

    this.audienceInfo = audienceInfo;
    const entity = this.lineItem || this.plan;
    const isSingleHivestackDeal = this.package != null && this.lineItem != null;
    const isMultipleHivestackDeals =
      entity != null &&
      (entity.packageID != null ||
        entity.packageGroupID != null ||
        entity.packageGroupDealsIDs?.length > 0 ||
        entity.packageIDs?.length > 0);

    // If fixed CPM or buying deal(s)
    if ((this.isCPMFromRateCard || isSingleHivestackDeal || isMultipleHivestackDeals) && cpmInfo != null) {
      let estimatedCpm = null;
      if (cpmInfo.min != null) {
        estimatedCpm = cpmInfo.min === cpmInfo.max ? cpmInfo.min : [cpmInfo.min, cpmInfo.max];
      } else if (cpmInfo.buyer_min_cpm != null) {
        estimatedCpm =
          cpmInfo.buyer_min_cpm === cpmInfo.buyer_max_cpm
            ? [cpmInfo.buyer_min_cpm]
            : [cpmInfo.buyer_min_cpm, cpmInfo.buyer_max_cpm];
      } else if (cpmInfo.owner_min_cpm != null) {
        estimatedCpm =
          cpmInfo.owner_min_cpm === cpmInfo.owner_max_cpm
            ? [cpmInfo.owner_min_cpm]
            : [cpmInfo.owner_min_cpm, cpmInfo.owner_max_cpm];
      }

      this.estimatedAverageCPM = cpmInfo.average;
      if (this.estimatedAverageCPM == null) {
        this.estimatedAverageCPM =
          cpmInfo.buyer_average_cpm != null ? cpmInfo.buyer_average_cpm : cpmInfo.owner_average_cpm;
      }
      if (this.estimatedAverageCPM == null && estimatedCpm != null) {
        this.estimatedAverageCPM = estimatedCpm.reduce((acc, val) => acc + val, 0) / estimatedCpm.length;
      }

      let estimatedAudienceCPM = null;
      if (estimatedCpm != null && audienceInfo != null) {
        const totalImpressions = audienceInfo.impressions;
        const audienceImpressions = audienceInfo.demographicImpressions;
        if (audienceInfo != null && totalImpressions != null && audienceImpressions != null) {
          estimatedAudienceCPM =
            estimatedCpm instanceof Array
              ? estimatedCpm.map((cpm) => (cpm * totalImpressions) / Math.max(1, audienceImpressions))
              : [(estimatedCpm * totalImpressions) / Math.max(1, audienceImpressions)];
          this.estimatedAverageAudienceCPM =
            (this.estimatedAverageCPM * totalImpressions) / Math.max(1, audienceImpressions);
        } else {
          estimatedAudienceCPM = estimatedCpm;
          this.estimatedAverageAudienceCPM = this.estimatedAverageCPM;
        }

        this.estimatedAudienceCPM = estimatedAudienceCPM;
        this.estimatedCPM =
          estimatedCpm instanceof Array
            ? estimatedCpm.map((cpm) => numbro(cpm).value())
            : [numbro(estimatedCpm).value()];
      }
    } else {
      const cpm = this.computedCPM == null ? [0] : this.computedCPM;
      const avgCPM = this.computedAverageCPM == null ? 0 : this.computedAverageCPM;
      if (audienceInfo != null) {
        const totalImpressions = audienceInfo.impressions;
        const audienceImpressions = audienceInfo.demographicImpressions;
        this.estimatedAudienceCPM = cpm.map((x) => (x * totalImpressions) / Math.max(1, audienceImpressions));
        this.estimatedAverageAudienceCPM = (avgCPM * totalImpressions) / Math.max(1, audienceImpressions);
      } else {
        this.estimatedAudienceCPM = cpm;
        this.estimatedAverageAudienceCPM = avgCPM;
      }
      this.estimatedCPM = cpm;
      this.estimatedAverageCPM = avgCPM;
    }

    this.storeCPMValues();
    if (this.plan?.hasMarkup && !this.plan?.isOverrideMarkup) {
      this.applyMarkup();
    } else {
      this.updateBudgetAndImpressions();
    }
  }

  calculateTargetImpressions() {
    if (this.isCPMFromRateCard) {
      if (this.estimatedAverageCPM != null) {
        if (this.budgetMode === 'B') {
          const budget = this.budget !== undefined && this.budget != null ? this.budget : 0;
          const impressions =
            this.estimatedAverageCPM > 0 ? [Math.round((budget / this.estimatedAverageCPM) * 1000)] : null;
          if (impressions !== this.targetImpressions) {
            this.updateTargetImpressions(impressions);
          }
        }
      }
    } else if (this.budgetMode === 'B') {
      const impressions = this.CPM == null ? null : this.CPM === 0 ? 0 : Math.round((this.budget / this.CPM) * 1000);
      if (impressions != null && impressions !== this.targetImpressions) {
        this.updateTargetImpressions(impressions);
      }
    }
  }

  calculateBudget() {
    if (this.isCPMFromRateCard) {
      if (this.estimatedAverageCPM != null) {
        if (this.budgetMode === 'I') {
          const budget = Math.round((this.targetImpressions / 1000.0) * this.estimatedAverageCPM * 100) / 100;
          if (budget != null && budget !== this.budget) {
            this.updateBudget(budget);
          }
        }
      }
    } else if (this.budgetMode === 'I') {
      const budget =
        this.computedDiscountedAverageCPM == null
          ? null
          : Math.round((this.targetImpressions * this.computedDiscountedAverageCPM) / 1000);
      if (budget != null && budget !== this.budget) {
        this.updateBudget(budget);
      }
    }
  }

  updateBudgetAndImpressions() {
    if (this.lineItem == null || this.plan == null) {
      return;
    }
    this.calculateBudget();
    this.calculateTargetImpressions();
  }

  updateCPM(value) {
    if (this.plan != null) {
      rootStore.dispatch('updatePlanCPM', { plan: this.plan, value });
    } else {
      rootStore.dispatch('updateLineItemCPM', { lineItem: this.lineItem, value });
    }
    this.estimatedCPM = [value];
    this.estimatedAverageCPM = value;
    this.updateBudgetAndImpressions();
  }

  updateBudget(value) {
    if (this.plan != null) {
      rootStore.dispatch('updatePlanBudget', { plan: this.plan, value });
    } else {
      rootStore.dispatch('updateLineItemBudget', { lineItem: this.lineItem, value });
    }
    // if the value is null, we still update the target impressions as the budget should now be 0
    if (value === null || value?.constructor !== Array) {
      this.calculateTargetImpressions();
    }
  }

  updateTargetImpressions(value) {
    if (this.plan != null) {
      rootStore.dispatch('updatePlanTargetImpressions', { plan: this.plan, value });
    } else {
      rootStore.dispatch('updateLineItemTargetImpressions', { lineItem: this.lineItem, value });
    }
    if (value != null && value.constructor !== Array) {
      this.calculateBudget();
    }
  }

  updateIsCPMFromRateCard(value) {
    if (this.plan != null) {
      rootStore.dispatch('updatePlanIsCPMFromRateCard', { plan: this.plan, value });
    } else {
      rootStore.dispatch('updateLineItemIsCPMFromRateCard', { lineItem: this.lineItem, value });
    }
    if (!value) {
      this.estimatedCPM = [this.CPM];
      this.estimatedAverageCPM = this.CPM;
    } else {
      this.updateCPM(null);
    }
    this.updateBudgetAndImpressions();
  }

  // --------------------------------------------------------------------------
  // Estimated CPM, used in the Estimated Stats widget.
  //

  get estimatedCPM() {
    return this.entity.estimatedCPM;
  }

  set estimatedCPM(value) {
    if (this.plan != null) {
      rootStore.dispatch('updatePlanEstimatedCPM', { plan: this.plan, value });
    } else if (this.lineItem != null) {
      rootStore.dispatch('updateLineItemEstimatedCPM', { lineItem: this.lineItem, value });
    } else {
      rootStore.dispatch('updatePackageEstimatedCPM', { package: this.package, value });
    }
    this.updateBudgetAndImpressions();
  }

  get estimatedAverageCPM() {
    if (this.lineItem != null) {
      return this.lineItem.estimatedAverageCPM;
    }
    if (this.package != null) {
      return this.package.estimatedAverageCPM;
    }
    return this.plan.estimatedAverageCPM;
  }

  set estimatedAverageCPM(value) {
    if (this.plan != null) {
      rootStore.dispatch('updatePlanEstimatedAverageCPM', { plan: this.plan, value });
    } else if (this.lineItem != null) {
      rootStore.dispatch('updateLineItemEstimatedAverageCPM', { lineItem: this.lineItem, value });
    } else {
      rootStore.dispatch('updatePackageEstimatedAverageCPM', { package: this.package, value });
    }
    this.updateBudgetAndImpressions();
  }

  get estimatedAudienceCPM() {
    if (this.lineItem != null) {
      return this.lineItem.estimatedAudienceCPM;
    }
    if (this.package != null) {
      return this.package.estimatedAudienceCPM;
    }
    return this.plan.estimatedAudienceCPM;
  }

  set estimatedAudienceCPM(value) {
    if (this.plan != null) {
      rootStore.dispatch('updatePlanEstimatedAudienceCPM', { plan: this.plan, value });
    } else if (this.lineItem != null) {
      rootStore.dispatch('updateLineItemEstimatedAudienceCPM', { lineItem: this.lineItem, value });
    } else {
      rootStore.dispatch('updatePackageEstimatedAudienceCPM', { package: this.package, value });
    }
  }

  get estimatedAverageAudienceCPM() {
    if (this.lineItem != null) {
      return this.lineItem.estimatedAverageAudienceCPM;
    }
    if (this.package != null) {
      return this.package.estimatedAverageAudienceCPM;
    }
    return this.plan.estimatedAverageAudienceCPM;
  }

  set estimatedAverageAudienceCPM(value) {
    if (this.plan != null) {
      rootStore.dispatch('updatePlanEstimatedAverageAudienceCPM', { plan: this.plan, value });
    } else if (this.lineItem != null) {
      rootStore.dispatch('updateLineItemEstimatedAverageAudienceCPM', { lineItem: this.lineItem, value });
    } else {
      rootStore.dispatch('updatePackageEstimatedAverageAudienceCPM', { package: this.package, value });
    }
  }

  get discountedEstimatedCPM() {
    if (this.estimatedCPM == null) {
      return null;
    }
    if (this.lineItem != null) {
      return this.estimatedCPM.map((x) => x * (1 - this.lineItem.totalDiscount));
    }
    return this.estimatedCPM;
  }

  //
  // Used by TruBid
  //
  get discountedEstimatedAverageCPM() {
    if (this.estimatedAverageCPM == null) {
      return null;
    }
    if (this.lineItem != null) {
      return this.estimatedAverageCPM * (1 - this.lineItem.totalDiscount);
    }
    return this.estimatedAverageCPM;
  }

  //
  // Used by EditLineItemV2 to display the CPM when it's based on Rate Card / Package
  //
  get estimatedCPMFormattedNoCurrency() {
    if (this.estimatedCPM == null) {
      return DASH;
    }
    if (this.estimatedAverageCPM != null) {
      return formatNumber(this.estimatedAverageCPM, '0,0.00');
    }
    const cpmFormatted = formatNumber(this.estimatedCPM[0], '0,0.00');
    const cpmFormatted2 = this.estimatedCPM.length > 1 ? formatNumber(this.estimatedCPM[1], '0,0.00') : '';
    return this.estimatedCPM.length === 1 || (this.estimatedCPM.length === 2 && cpmFormatted == cpmFormatted2)
      ? cpmFormatted
      : `${cpmFormatted} – ${cpmFormatted2}`;
  }

  //
  // Used in LineItemDetails, in KPIS (eCPM / discounted CPM)
  // Used in Estimated Stats when CPM is from rate card
  //
  get discountedEstimatedCPMFormatted() {
    if (
      (this.plan == null && this.discountedEstimatedCPM == null) ||
      (this.plan != null && this.discountedEstimatedAverageCPM == null)
    ) {
      return DASH;
    }
    if (this.discountedEstimatedAverageCPM != null) {
      return i18n.global.n(this.discountedEstimatedAverageCPM, 'currency');
    }
    const cpmFormatted = i18n.global.n(this.discountedEstimatedCPM[0], 'currency');
    const cpmFormatted2 =
      this.discountedEstimatedCPM.length > 1 ? i18n.global.n(this.discountedEstimatedCPM[1], 'currency') : '';
    const discountedEstimatedCPMFormatted =
      this.discountedEstimatedCPM.length === 1 ||
      (this.discountedEstimatedCPM.length === 2 && cpmFormatted == cpmFormatted2)
        ? cpmFormatted
        : `${cpmFormatted} – ${cpmFormatted2}`;
    return discountedEstimatedCPMFormatted;
  }

  //
  // Used in plan exports.
  //
  get discountedEstimatedCPMNoFormating() {
    if (
      (this.plan == null && this.discountedEstimatedCPM == null) ||
      (this.plan != null && this.discountedEstimatedAverageCPM == null)
    ) {
      return null;
    }
    if (this.discountedEstimatedAverageCPM != null) {
      return this.discountedEstimatedAverageCPM;
    }
    const cpm = this.discountedEstimatedCPM[0];
    const cpm2 = this.discountedEstimatedCPM.length > 1 ? this.discountedEstimatedCPM[1] : null;
    const discountedEstimatedCPM =
      this.discountedEstimatedCPM.length === 1 || (this.discountedEstimatedCPM.length === 2 && cpm === cpm2)
        ? cpm
        : [cpm, cpm2];
    return discountedEstimatedCPM;
  }

  //
  // Used by EditLineItemV2 to display the discounted CPM in read-only field
  //
  get discountedEstimatedCPMFormattedNoCurrency() {
    if (this.discountedEstimatedCPM == null) {
      return DASH;
    }
    if (this.discountedEstimatedAverageCPM != null) {
      return formatNumber(this.discountedEstimatedAverageCPM, '0,0.00');
    }
    const cpmFormatted = formatNumber(this.discountedEstimatedCPM[0], '0,0.00');
    const cpmFormatted2 =
      this.discountedEstimatedCPM.length > 1 ? formatNumber(this.discountedEstimatedCPM[1], '0,0.00') : '';
    return this.discountedEstimatedCPM.length === 1 ||
      (this.discountedEstimatedCPM.length === 2 && cpmFormatted == cpmFormatted2)
      ? cpmFormatted
      : `${cpmFormatted} – ${cpmFormatted2}`;
  }

  //
  // Used by Plan - Markup CPM
  //
  storeCPMValues() {
    this.eCPMWithNoMarkup = this.discountedEstimatedCPMFormatted;
    this.storedEstimatedCPM = this.estimatedCPM;
    this.storedEstimatedAverageCPM = this.estimatedAverageCPM;
    this.storedEstimatedAudienceCPM = this.estimatedAudienceCPM;
    this.storedEstimatedAverageAudienceCPM = this.estimatedAverageAudienceCPM;
  }

  applyMarkup() {
    const getNewEcpmWithMarkupApplied = (ecpm) => {
      if (this.plan.isPercentMarkup) {
        const percentageCpmMarkup = this.plan.planFeeValue / 100;
        return ecpm + percentageCpmMarkup * ecpm;
      }
      return ecpm + this.plan.planFeeValue;
    };

    this.estimatedCPM = this.storedEstimatedCPM?.map((x) => getNewEcpmWithMarkupApplied(x)) || [0];
    this.estimatedAverageCPM = getNewEcpmWithMarkupApplied(this.storedEstimatedAverageCPM);
    this.estimatedAudienceCPM = getNewEcpmWithMarkupApplied(this.storedEstimatedAudienceCPM);
    this.estimatedAverageAudienceCPM = getNewEcpmWithMarkupApplied(this.storedEstimatedAverageAudienceCPM);
  }
}
